import React from 'react';
import { AlertCircle, Check } from 'lucide-react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  type?: 'info' | 'success' | 'error' | 'confirm';
  title?: string;
  message?: string;
  onConfirm?: () => void | false;
  confirmText?: string;
  cancelText?: string;
  inputLabel?: string;
  inputPlaceholder?: string;
  inputValue?: string;
  onInputChange?: (value: string) => void;
}

const typeStyles = {
  info: {
    icon: AlertCircle,
    iconColor: 'text-accent-500 dark:text-accent-400',
    bgColor: 'bg-accent-50 dark:bg-accent-900/30',
    borderColor: 'border-accent-200 dark:border-accent-800',
  },
  success: {
    icon: Check,
    iconColor: 'text-success-500 dark:text-success-400',
    bgColor: 'bg-success-50 dark:bg-success-900/30',
    borderColor: 'border-success-200 dark:border-success-800',
  },
  error: {
    icon: AlertCircle,
    iconColor: 'text-danger-500 dark:text-danger-400',
    bgColor: 'bg-danger-50 dark:bg-danger-900/30',
    borderColor: 'border-danger-200 dark:border-danger-800',
  },
  confirm: {
    icon: AlertCircle,
    iconColor: 'text-ink-muted',
    bgColor: 'bg-raised',
    borderColor: 'border-line',
  },
};

export default function Modal({
  isOpen,
  onClose,
  type = 'info',
  title,
  message,
  onConfirm,
  confirmText = 'OK',
  cancelText = 'Cancel',
  inputLabel,
  inputPlaceholder,
  inputValue,
  onInputChange,
}: ModalProps) {
  if (!isOpen) return null;

  const style = typeStyles[type] || typeStyles.info;
  const Icon = style.icon;
  const hasInput = Boolean(inputLabel || inputPlaceholder);

  const handleConfirm = () => {
    if (onConfirm) {
      const result = onConfirm();
      if (result === false) return;
    }
    onClose();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && hasInput) handleConfirm();
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 dark:bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-panel border border-line rounded-[40px] shadow-2xl max-w-sm w-full p-6 animate-in fade-in duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`w-12 h-12 rounded-full ${style.bgColor} flex items-center justify-center mx-auto mb-4`}>
          <Icon className={`w-6 h-6 ${style.iconColor}`} />
        </div>
        {title && <h3 className="text-xl font-bold text-ink text-center mb-2">{title}</h3>}
        {message && <p className="text-ink-muted text-center mb-6">{message}</p>}
        {hasInput && (
          <div className="mb-6">
            {inputLabel && <label className="block text-sm font-medium text-ink mb-2">{inputLabel}</label>}
            <input
              type="text"
              value={inputValue || ''}
              onChange={(e) => onInputChange?.(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={inputPlaceholder}
              className="w-full px-4 py-2.5 rounded-xl bg-raised border border-line-strong text-ink placeholder:text-ink-faint focus:outline-none focus:ring-2 focus:ring-accent-500"
              autoFocus
            />
          </div>
        )}
        <div className="flex gap-3">
          {(type === 'confirm' || hasInput) && (
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-full font-medium text-ink-muted bg-raised hover:bg-line transition-colors"
            >
              {cancelText}
            </button>
          )}
          <button
            onClick={handleConfirm}
            className={`flex-1 px-4 py-2.5 rounded-full font-bold text-white transition-colors ${
              type === 'error'
                ? 'bg-danger-600 hover:bg-danger-700 dark:bg-danger-500 dark:hover:bg-danger-600'
                : type === 'success'
                ? 'bg-success-600 hover:bg-success-700 dark:bg-success-500 dark:hover:bg-success-600'
                : type === 'confirm'
                ? 'bg-accent-600 hover:bg-accent-500'
                : 'bg-accent-600 hover:bg-accent-500'
            }`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
